import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { buildCanonicalUrl, normalizeSeoPath, shouldNoindexPath } from '@/seo';
interface MetaTagsProps {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: string;
    path?: string;
}
const MetaTags: React.FC<MetaTagsProps> = ({ title, description, keywords, ogImage, ogType = 'website', path }) => {
    const location = useLocation();
    useEffect(() => {
        const currentPath = path || location.pathname;
        const normalizedPath = normalizeSeoPath(currentPath);
        const fullUrl = buildCanonicalUrl(normalizedPath);
        const noindex = shouldNoindexPath(normalizedPath);
        if (title) {
            document.title = title;
        }
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription && description) {
            metaDescription.setAttribute('content', description);
        }
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (keywords) {
            if (!metaKeywords) {
                metaKeywords = document.createElement('meta');
                metaKeywords.setAttribute('name', 'keywords');
                document.head.appendChild(metaKeywords);
            }
            metaKeywords.setAttribute('content', keywords);
        }
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle && title) {
            ogTitle.setAttribute('content', title);
        }
        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription && description) {
            ogDescription.setAttribute('content', description);
        }
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) {
            ogUrl.setAttribute('content', fullUrl);
        }
        else {
            const newOgUrl = document.createElement('meta');
            newOgUrl.setAttribute('property', 'og:url');
            newOgUrl.setAttribute('content', fullUrl);
            document.head.appendChild(newOgUrl);
        }
        const ogTypeMeta = document.querySelector('meta[property="og:type"]');
        if (ogTypeMeta) {
            ogTypeMeta.setAttribute('content', ogType);
        }
        if (ogImage) {
            const ogImageMeta = document.querySelector('meta[property="og:image"]');
            if (ogImageMeta) {
                ogImageMeta.setAttribute('content', ogImage);
            }
            else {
                const newOgImage = document.createElement('meta');
                newOgImage.setAttribute('property', 'og:image');
                newOgImage.setAttribute('content', ogImage);
                document.head.appendChild(newOgImage);
            }
        }
        const twitterCard = document.querySelector('meta[name="twitter:card"]');
        if (!twitterCard) {
            const newTwitterCard = document.createElement('meta');
            newTwitterCard.setAttribute('name', 'twitter:card');
            newTwitterCard.setAttribute('content', 'summary_large_image');
            document.head.appendChild(newTwitterCard);
        }
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle && title) {
            twitterTitle.setAttribute('content', title);
        }
        else if (title) {
            const newTwitterTitle = document.createElement('meta');
            newTwitterTitle.setAttribute('name', 'twitter:title');
            newTwitterTitle.setAttribute('content', title);
            document.head.appendChild(newTwitterTitle);
        }
        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription && description) {
            twitterDescription.setAttribute('content', description);
        }
        else if (description) {
            const newTwitterDescription = document.createElement('meta');
            newTwitterDescription.setAttribute('name', 'twitter:description');
            newTwitterDescription.setAttribute('content', description);
            document.head.appendChild(newTwitterDescription);
        }
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) {
            robotsMeta.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
        }
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (noindex) {
            canonicalLink?.remove();
        }
        else if (canonicalLink) {
            canonicalLink.setAttribute('href', fullUrl);
        }
        else {
            const newCanonical = document.createElement('link');
            newCanonical.setAttribute('rel', 'canonical');
            newCanonical.setAttribute('href', fullUrl);
            document.head.appendChild(newCanonical);
        }
    }, [title, description, keywords, ogImage, ogType, location.pathname, path]);
    return null;
};
export default MetaTags;
